import React from "react";
import queryString from "query-string";
import { Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { connect, useSelector } from "react-redux";
import { RouteComponentProps, withRouter, useLocation } from "react-router-dom";
import {
  DocuClipperBillingCycle,
  DocuClipperPlan,
  StripePlan,
  StripeSubscription,
} from "../../../../docuclipper/DocuclipperTypes";
import { createAlert, CreateAlertFn } from "../../../../redux/reducers/Alerts";
import ConfirmPlan from "../SelectPlanAfterTrial/ConfirmPlan";
import {
  getDiscountedPricing,
  getPricing,
  WithAccountAndPlans,
} from "../utils";
import { ConfirmDowngrade } from "./ConfirmDowngrade";
import PlanSelector2 from "../SelectPlanAfterTrial/PlanSelector2";
import { useSeat } from "../../../../custom-hooks/useSeat";
import { ReduxState } from "src/redux";
import { sendPageViewToBackend } from "src/docuclipper/api";
import { groupPlansByTiers, getParentPlanName } from "../SelectPlanAfterTrial/tierUtils";

type Props = {
  subscription: StripeSubscription;
  plans: StripePlan[];
  onConfirmed: () => any;
};
type ReduxProps = {
  createAlert: CreateAlertFn;
};

const SwitchPlans2: React.FC<Props & ReduxProps & RouteComponentProps> = ({
  subscription,
  plans,
  history,
  createAlert,
  onConfirmed,
}) => {
  const location = useLocation();
  const [view, setView] = React.useState<
    "select" | "confirmdowngrade" | "confirm"
  >("select");
  const [selectedPlan, setSelectedPlan] = React.useState<StripePlan | null>(
    null
  );
  const [tierIndices, setTierIndices] = React.useState<Record<string, number>>(
    {}
  );
  const [upgradeToApplied, setUpgradeToApplied] = React.useState(false);

  React.useEffect(() => {
    if (upgradeToApplied || !plans?.length) return;
    const q = queryString.parse(location.search);
    const upgradeTo = q.upgradeTo as string | undefined;
    const billingCycle = (q.billingCycle as string) || "Monthly";
    if (!upgradeTo) return;
    const decoded = decodeURIComponent(upgradeTo);
    const plan = plans.find(
      (p) =>
        (p.docuclipperPlan.name as string) === decoded &&
        (p.docuclipperPlan.billingCycle as string) === billingCycle
    );
    if (!plan) return;
    const groups = groupPlansByTiers(plans);
    const indices: Record<string, number> = {};
    for (const group of groups) {
      const idx = group.tiers.findIndex((t) => t.numPages === plan.docuclipperPlan.numPages);
      if (idx >= 0) indices[group.parentPlan] = idx;
    }
    setSelectedPlan(plan);
    setTierIndices(indices);
    setView("confirm");
    setUpgradeToApplied(true);
  }, [plans, location.search, upgradeToApplied]);

  React.useEffect(() => {
    sendPageViewToBackend(`/account/changePlans?checkoutStage=${view}`);
  }, [view]);
  const pricing = getPricing(plans);
  if (!pricing) {
    return <Alert variant="danger">Error loading plans</Alert>;
  }

  const discountedPricing = getDiscountedPricing(pricing, subscription);

  return (
    <>
      {view === "select" && (
        <>
          <PlanSelector2
            plans={plans}
            initialTierIndices={tierIndices}
            headingOverride="Change your plan"
            onSelect={(plan: StripePlan, indices: Record<string, number>) => {
              let isDowngrade = false;
              if (subscription.status !== "canceled") {
                const newParent = getParentPlanName(plan);
                const currentParent = getParentPlanName(
                  // Build a minimal StripePlan-like object so getParentPlanName works
                  { docuclipperPlan: subscription.docuclipperPlan } as any
                );

                const TIER_ORDER = ["Starter", "Professional", "Business", "Enterprise"];
                const currentRank = TIER_ORDER.indexOf(currentParent);
                const newRank = TIER_ORDER.indexOf(newParent);
                if (currentRank > 0 && newRank >= 0 && newRank < currentRank) {
                  isDowngrade = true;
                }
              }
              setSelectedPlan(plan);
              setTierIndices(indices);
              setView(isDowngrade ? "confirmdowngrade" : "confirm");
            }}
            currentPlan={subscription.docuclipperPlan.name}
            currentBillingCycle={subscription.docuclipperPlan.billingCycle}
          />
        </>
      )}
      {view === "confirmdowngrade" && selectedPlan && (
        <ConfirmDowngrade
          subscription={subscription}
          plans={plans}
          selectedPlan={selectedPlan.docuclipperPlan.name}
          onConfirmed={() => setView("confirm")}
        />
      )}
      {view === "confirm" && selectedPlan && (
        <>
          {subscription.status === "active" && (
            <div
              className="mb-4 p-3 d-flex align-items-center"
              style={{
                background: "#f0f4ff",
                borderRadius: 8,
                fontSize: "0.95rem",
              }}
            >
              <span className="text-muted">Current:</span>
              <strong className="mx-2">{subscription.docuclipperPlan.name}</strong>
              <FontAwesomeIcon icon="arrow-right" className="text-primary mx-2" />
              <span className="text-muted">New:</span>
              <strong className="mx-2 text-primary">
                {selectedPlan.docuclipperPlan.name}
              </strong>
              <span className="text-muted ml-1">
                ({selectedPlan.docuclipperPlan.billingCycle})
              </span>
            </div>
          )}
          <ConfirmPlan
            plans={plans}
            pricing={pricing}
            discountedPricing={discountedPricing}
            selectedPlan={selectedPlan.docuclipperPlan.name}
            selectedBillingCycle={selectedPlan.docuclipperPlan.billingCycle}
            btnText="Confirm plan change"
            subscription={subscription}
            onConfirmed={onConfirmed}
            onBack={() => setView("select")}
          />
        </>
      )}
    </>
  );
};

export const _SwitchPlans2: React.FC<
  Props &
    ReduxProps &
    RouteComponentProps & {
      redirectToAccountOnConfirmed: boolean;
      onConfirmed?: () => any;
    }
> = ({ history, redirectToAccountOnConfirmed, onConfirmed, ...rest }) => {
  const { isSeat, admin } = useSeat();
  const isContractAdmin = useSelector(
    (state: ReduxState) => state.User.user?.isContractAdmin
  );

  if (!isContractAdmin) {
    let email = "";
    if (admin && admin.email) {
      email = admin.email;
    }
    return (
      <Alert variant="info">
        {email
          ? `Plan changes are managed by your account admin (${email}). Ask them to update the plan, or contact support@docuclipper.com if you need help.`
          : `Plan changes are managed by your account admin. Ask them to update the plan, or contact support@docuclipper.com if you're not sure who that is.`}
      </Alert>
    );
  }
  return (
    <SwitchPlans2
      {...rest}
      history={history}
      onConfirmed={() => {
        if (redirectToAccountOnConfirmed) {
          history.push("/account?section=plan");
        }
        onConfirmed();
      }}
    />
  );
};
export const SwitchPlans = withRouter(_SwitchPlans2);

SwitchPlans.defaultProps = {
  redirectToAccountOnConfirmed: true,
  onConfirmed: () => null,
};

export default connect((state) => ({}), { createAlert })(
  withRouter(WithAccountAndPlans(SwitchPlans))
);
